import { useCallback, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useApp } from '../context/AppContext';

// Icons
const PlayIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const ResetIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
    />
  </svg>
);

export function YearScrubber() {
  const { state, data, setYear, togglePlaying, stopPlaying, dispatch } = useApp();
  const sliderRef = useRef<HTMLInputElement>(null);
  const yearDisplayRef = useRef<HTMLSpanElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  
  const years = data?.years ?? [];
  const minYear = years[0] ?? 2007;
  const maxYear = years[years.length - 1] ?? 2025;
  
  // Calculate progress percentage
  const progress = years.length > 1
    ? ((state.selectedYear - minYear) / (maxYear - minYear)) * 100
    : 0;
  
  // Handle slider change
  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const year = parseInt(e.target.value, 10);
    setYear(year);
    
    // Animate year display
    if (yearDisplayRef.current) {
      gsap.fromTo(yearDisplayRef.current, 
        { scale: 1.2, color: '#22d3ee' },
        { scale: 1, color: '#f1f5f9', duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [setYear]);
  
  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (state.isPlaying) {
      stopPlaying();
    } else {
      // If at the end, reset to beginning
      if (state.selectedYear === maxYear) {
        setYear(minYear);
      }
      togglePlaying();
    }
  }, [state.isPlaying, state.selectedYear, maxYear, minYear, stopPlaying, togglePlaying, setYear]);
  
  // Handle reset
  const handleReset = useCallback(() => {
    stopPlaying();
    setYear(minYear);
  }, [stopPlaying, setYear, minYear]);
  
  // Handle speed change
  const handleSpeedChange = useCallback((speed: number) => {
    dispatch({ type: 'SET_PLAY_SPEED', payload: speed });
  }, [dispatch]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (state.selectedYear > minYear) {
            setYear(state.selectedYear - 1);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (state.selectedYear < maxYear) {
            setYear(state.selectedYear + 1);
          }
          break;
        case 'Home':
          e.preventDefault();
          setYear(minYear);
          break;
        case 'End':
          e.preventDefault();
          setYear(maxYear);
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePlayPause, state.selectedYear, minYear, maxYear, setYear]);
  
  // Animate progress bar
  useEffect(() => {
    if (progressRef.current) {
      gsap.to(progressRef.current, {
        width: `${progress}%`,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }, [progress]);
  
  return (
    <div className="card p-4 space-y-4">
      {/* Header with year display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePlayPause}
            className="btn-primary p-3 rounded-full"
            aria-label={state.isPlaying ? 'Pause' : 'Play'}
          >
            {state.isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          
          <button
            onClick={handleReset}
            className="btn-ghost p-2 rounded-full"
            aria-label="Reset to beginning"
          >
            <ResetIcon />
          </button>
        </div>
        
        <div className="text-center">
          <span
            ref={yearDisplayRef}
            className="text-4xl font-display font-bold text-surface-100 tabular-nums"
          >
            {state.selectedYear}
          </span>
        </div>
        
        {/* Speed controls */}
        <div className="flex items-center gap-1">
          {[
            { label: '0.5x', value: 2000 },
            { label: '1x', value: 1000 },
            { label: '2x', value: 500 },
          ].map(({ label, value }) => (
            <button
              key={value}
              onClick={() => handleSpeedChange(value)}
              className={`px-2 py-1 text-xs rounded ${
                state.playSpeed === value
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-surface-400 hover:text-surface-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Progress bar and slider */}
      <div className="relative">
        {/* Background track */}
        <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
          <div
            ref={progressRef}
            className="h-full bg-gradient-to-r from-accent-600 to-accent-400 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Slider input (invisible, for interaction) */}
        <input
          ref={sliderRef}
          type="range"
          min={minYear}
          max={maxYear}
          step={1}
          value={state.selectedYear}
          onChange={handleSliderChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label="Select year"
        />
        
        {/* Thumb indicator */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-accent-400 rounded-full shadow-glow pointer-events-none transition-all duration-150"
          style={{ left: `calc(${progress}% - 10px)` }}
        />
      </div>
      
      {/* Year markers */}
      <div className="flex justify-between text-xs text-surface-500">
        <span>{minYear}</span>
        <span>{Math.round((minYear + maxYear) / 2)}</span>
        <span>{maxYear}</span>
      </div>
      
      {/* Keyboard hints */}
      <div className="flex items-center justify-center gap-4 text-2xs text-surface-500">
        <span>
          <kbd className="px-1.5 py-0.5 bg-surface-800 rounded text-surface-400">Space</kbd>
          {' '}Play/Pause
        </span>
        <span>
          <kbd className="px-1.5 py-0.5 bg-surface-800 rounded text-surface-400">←</kbd>
          <kbd className="px-1.5 py-0.5 bg-surface-800 rounded text-surface-400 ml-1">→</kbd>
          {' '}Navigate
        </span>
      </div>
    </div>
  );
}
